import { useState } from 'react';
import {
  useExportEmployeeActionsHistory,
  useExportEmployeeActivitiesHistory,
  useExportEmployeeCommunicationsHistory,
} from '@/api/employees/employees.downloads';
import type { EmployeeWithBeneficiariesFormatted } from '@/api/employees/employees.types';
import { DatePicker } from '@/components/DatePicker';
import { ExportButton } from '@/components/ExportButton';
import { SearchInput } from '@/components/SearchInput';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { EmployeeActions } from './EmployeeActions';
import { EmployeeActivities } from './EmployeeActivities';
import { EmployeeCommunications } from './EmployeeCommunications';

interface EmployeeHistoryProps {
  employee: EmployeeWithBeneficiariesFormatted;
}

enum EmployeeHistoryTabs {
  Interventions = 'Interventions',
  Activities = 'Activités',
  RemindersAndCommunications = 'Relances et échanges',
}

export function EmployeeHistory(props: EmployeeHistoryProps) {
  const { employee } = props;
  const [currentTab, setCurrentTab] = useState<EmployeeHistoryTabs>(
    EmployeeHistoryTabs.Interventions
  );
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const { download: exportEmployeeActionsHistory } = useExportEmployeeActionsHistory(employee.id);
  const { download: exportEmployeeActivitiesHistory } = useExportEmployeeActivitiesHistory(
    employee.id
  );
  const { download: exportEmployeeCommunicationsHistory } = useExportEmployeeCommunicationsHistory(
    employee.id
  );

  const tabDatas: Record<EmployeeHistoryTabs, { component: React.ReactNode; export: () => void }> =
    {
      [EmployeeHistoryTabs.Interventions]: {
        component: <EmployeeActions employee={employee} search={search} dateFilter={dateFilter} />,
        export: exportEmployeeActionsHistory,
      },
      [EmployeeHistoryTabs.Activities]: {
        component: (
          <EmployeeActivities employee={employee} search={search} dateFilter={dateFilter} />
        ),
        export: exportEmployeeActivitiesHistory,
      },
      [EmployeeHistoryTabs.RemindersAndCommunications]: {
        component: (
          <EmployeeCommunications employee={employee} search={search} dateFilter={dateFilter} />
        ),
        export: exportEmployeeCommunicationsHistory,
      },
    };
  const tabData = tabDatas[currentTab];

  return (
    <Tabs
      defaultValue={currentTab}
      onValueChange={(value) => setCurrentTab(value as EmployeeHistoryTabs)}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 justify-between">
          <TabsList variant="minimal">
            {[
              EmployeeHistoryTabs.Interventions,
              EmployeeHistoryTabs.Activities,
              EmployeeHistoryTabs.RemindersAndCommunications,
            ].map((tab) => {
              return (
                <TabsTrigger key={tab} value={tab} variant="minimal">
                  {tab}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="flex items-center gap-2">
            <DatePicker value={dateFilter} onChange={setDateFilter} />
            <SearchInput search={search} setSearch={setSearch} />
            <ExportButton onClick={tabData.export} />
          </div>
        </div>
        {tabData.component}
      </div>
    </Tabs>
  );
}
